/**
 * DEV ONLY — preview route for LP comparison study results.
 * Renders ReportLandingPage with hardcoded fixture data so the full report UI
 * can be reviewed without needing to run a real study.
 *
 * Route: /internal/lp-preview
 */
import { ReportLandingPage } from '@/features/studies/report/page/components/ReportLandingPage'
import type { Deck } from '@digital-twins/deck-ui/slides/contracts'
import type { CanonicalAnalysisOutput } from '@digital-twins/shared-types'

// ── Fixture: LP concept image URLs ───────────────────────────────────────────
//
// Placehold.co images simulate uploaded LP screenshots.
// Replace with real screenshot URLs to see the actual landing pages.
const LP_IMAGE_B = 'https://placehold.co/800x500/0f172a/94a3b8?text=Redesign+Concept+B'
const LP_IMAGE_A = 'https://placehold.co/800x500/f1f5f9/334155?text=Current+Homepage'
const LP_IMAGE_C = 'https://placehold.co/800x500/f0fdf4/166534?text=Minimal+Variant+C'

// ── Twin persona names ────────────────────────────────────────────────────────
//
// Digital twins have real persona names derived from the audience profile.
// Use these in all quote attributions instead of "Respondent X".
// In production, names come from the respondent registry.
//
//   Alex Kim          — Director of Sales, TechCorp
//   Sarah Chen        — VP of Marketing, ScaleUp Inc
//   Marcus Williams   — Director of Growth, Acme SaaS
//   Priya Patel       — C-Suite (VP of Product), StartupXYZ
//   James Rodriguez   — Manager, Engineering, BigCorp
//   Emma Thompson     — Director of Operations, Enterprise Co

// ── Fixture: full LP comparison deck ─────────────────────────────────────────
//
// Simulates the complete deck a real LP comparison study would generate.
// Covers all v1 required outputs from §8 of the LP Testing Spec.
//
// Scalability: every slide works for 1–6 landing pages:
//   • podium — up to 6 entries (top 3 shown, rest in footnote)
//   • quadrant-map — up to 10 points
//   • matrix-rating — up to 12 rows × 8 measures (pages=rows for scorecard;
//                     segments=rows for the segment heatmap, pages=measures)
//   • single-select-bars — up to 6 bars
//   • qual-analysis — themes are cross-LP; LP is always explicit in each quote
//
// Slide order (13 slides, spec §8 output coverage):
//   1.  cover
//   2.  podium                  — Winner verdict (Spec: "Winner")
//   3.  exec-summary            — SCR narrative
//   4.  matrix-rating           — 3 decision scores per LP (Spec: "3 scores/page")
//   5.  quadrant-map            — Comprehension × Conversion Intent (Spec: "Hero alt.")
//   6.  single-select-bars      — 5-second test: % understood per LP (Spec: "5-second test")
//   7.  qual-analysis (neg)     — Objections: what would stop you per LP (Spec: "Objections")
//   8.  matrix-rating           — Segment × LP heatmap (Spec: "Segments")
//   9.  matrix-rating           — Full per-metric scorecard (Spec: "Full scorecard")
//   10. single-select-bars      — Explicit preference tiebreaker
//   11. qual-analysis (pos)     — What resonated — positive themes
//   12. recommendations
//   13. outro

const FIXTURE_DECK: Deck = {
  id: 'preview-deck',
  experimentId: 'preview-exp',
  title: 'Homepage Redesign A/B Test',
  createdAt: new Date().toISOString(),
  status: 'completed',
  metadata: {
    conceptImages: [LP_IMAGE_B, LP_IMAGE_A, LP_IMAGE_C],
  },
  slides: [
    // ── 1. Cover ───────────────────────────────────────────────────────────
    {
      type: 'cover',
      title: 'Homepage Redesign A/B Test',
      subtitle: 'Which landing page converts better?',
      eyebrow: 'LP Comparison Study · 3 variants · n=30',
      authorName: null,
      meta: { date: new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) },
    } as any,

    // ── 2. Podium — Winner verdict ─────────────────────────────────────────
    // Spec: "Winner | Podium (ranked cards + hero thumbnails)"
    // Primary ranking dimension = Conversion Intent score (the computed winner,
    // equivalent to Zappi's Sales Impact Score).  The podium is DERIVED from
    // the decision scores — not from the Q16 preference question.
    // Scales 1-6 LPs: the renderer shows all entries; top-3 are highlighted.
    {
      type: 'podium',
      title: 'Conversion Intent Score',
      eyebrow: 'Winner',
      actionTitle: 'Redesign B earns the highest Conversion Intent score — ship it',
      scoreLabel: 'Conversion Intent',
      entries: [
        { id: 'asset_b', rank: 1, label: 'Redesign Concept B', score: 74, imageUrl: LP_IMAGE_B, badgeLabel: 'B' },
        { id: 'asset_a', rank: 2, label: 'Current Homepage',   score: 61, imageUrl: LP_IMAGE_A, badgeLabel: 'A' },
        { id: 'asset_c', rank: 3, label: 'Minimal Variant C',  score: 55, imageUrl: LP_IMAGE_C, badgeLabel: 'C' },
      ],
    } as any,

    // ── 3. Exec Summary — SCR narrative ───────────────────────────────────
    // KPI strip is the new typed `kpis[]` contract (2 slots), not the legacy
    // `columns[].anchor` shape — see ExecSummarySlide's three render states.
    {
      type: 'exec-summary',
      eyebrow: 'Key Findings',
      actionTitle: 'Redesign B wins on conversion intent, clarity, and CTA',
      columns: [
        {
          sectionLabel: '1. Winner',
          narrative: '**Redesign B** leads on every conversion metric. Its headline is clearer, the CTA stands out, and visitors immediately understand the value proposition.',
          bullets: [
            '**Redesign B: 74 vs Current Homepage: 61 vs Minimal C: 55** — gap is consistent across segments',
            'Redesign B wins on 4 of 5 individual metrics; trust is the only near-tie',
            '64% of twins explicitly nominated Redesign B as their preferred page',
          ],
        },
        {
          sectionLabel: '2. Gap Drivers',
          narrative: 'The gap is driven by **headline clarity** (+16 pts) and **CTA effectiveness** (+14 pts). Current Homepage scores higher on trust alone — a familiar brand signal that Redesign B does not yet carry.',
          bullets: [
            '**Redesign B 81 vs Current Homepage 65** on Message Clarity — biggest single gap',
            '**Redesign B 72 vs Current Homepage 58** on CTA Effectiveness',
            '**Current Homepage 70 vs Redesign B 68** on Trust — the only metric Current Homepage wins',
          ],
        },
        {
          sectionLabel: '3. Action',
          narrative: 'Ship **Redesign B** as the primary variant. Graft the trust signals from **Current Homepage** (logo wall, rating chip) above the fold. Re-test **Minimal C** after headline iteration.',
          bullets: [
            'A/B ship Redesign B with Homepage trust block grafted in',
            'Address "information overload below the fold" (43% of twins flagged it)',
            'Re-test Minimal Variant C after headline and CTA copy revision',
          ],
        },
      ],
      kpis: [
        {
          value: 64,
          valueFormat: 'percent',
          unit: '%',
          sentence: 'Twins explicitly named Redesign B their preferred page over the field.',
          primarySourceId: 'asset_b',
          sourceIds: ['asset_b'],
          statistic: 'respondent_share',
          observationUnit: 'respondent',
          scope: 'asset:asset_b',
          validAnswerBase: 30,
          uniqueRespondents: 30,
        },
        {
          value: 16,
          valueFormat: 'points',
          unit: 'pp',
          sentence: 'Message clarity is the widest metric gap between Redesign B and Current Homepage.',
          primarySourceId: 'asset_b',
          sourceIds: ['asset_b', 'asset_a'],
          statistic: 'share_delta',
          observationUnit: 'respondent',
          scope: 'asset:asset_b',
          validAnswerBase: 30,
          uniqueRespondents: 30,
        },
      ],
    } as any,

    // ── 4. Matrix Rating — 3 decision scores per LP ───────────────────────
    // Spec: "3 scores/page | Radar + summary bar"
    // We use a heatmap (LPs as rows, composites as columns) which is more
    // readable for 1-6 LPs than a radar overlay.  The 3 composites are:
    //   Comprehension  = clarity + 5-sec + visual hierarchy + message match
    //   Conversion Intent = relevance + trust + (inv.)objection + next-step
    //   Desirability   = emotional affect + price/value (v1: partial)
    // Scales 1-6 LPs: rows grow, 3 columns stay fixed.
    {
      type: 'matrix-rating',
      eyebrow: 'Decision Scores',
      actionTitle: 'Redesign B leads on all three dimensions — strongest on Comprehension',
      actionSubtitle: '0-100 decision scores · Comprehension · Conversion Intent · Desirability · n=30',
      plotType: 'matrix_rating_heatmap',
      scale: { min: 0, max: 100, midpoint: 50 },
      measures: [
        { id: 'comp_cmp', label: 'Comprehension',    metricId: 'composite_lp_comprehension',      unit: 'rating_0_100', average: 64 },
        { id: 'comp_cvi', label: 'Conversion Intent', metricId: 'composite_lp_conversion_intent', unit: 'rating_0_100', average: 63 },
        { id: 'comp_des', label: 'Desirability',      metricId: 'composite_lp_desirability',       unit: 'rating_0_100', average: 63 },
      ],
      rows: [
        {
          id: 'asset_b', label: 'Redesign Concept B', average: 71.3, imageUrl: LP_IMAGE_B,
          cells: [
            { measureId: 'comp_cmp', value: 72, normalizedValue: 0.72, isStrongest: true  },
            { measureId: 'comp_cvi', value: 74, normalizedValue: 0.74, isStrongest: true  },
            { measureId: 'comp_des', value: 68, normalizedValue: 0.68, isStrongest: true  },
          ],
        },
        {
          id: 'asset_a', label: 'Current Homepage', average: 63.3, imageUrl: LP_IMAGE_A,
          cells: [
            { measureId: 'comp_cmp', value: 65, normalizedValue: 0.65, isStrongest: false },
            { measureId: 'comp_cvi', value: 61, normalizedValue: 0.61, isStrongest: false },
            { measureId: 'comp_des', value: 64, normalizedValue: 0.64, isStrongest: false },
          ],
        },
        {
          id: 'asset_c', label: 'Minimal Variant C', average: 55.7, imageUrl: LP_IMAGE_C,
          cells: [
            { measureId: 'comp_cmp', value: 54, normalizedValue: 0.54, isStrongest: false },
            { measureId: 'comp_cvi', value: 55, normalizedValue: 0.55, isStrongest: false },
            { measureId: 'comp_des', value: 58, normalizedValue: 0.58, isStrongest: false },
          ],
        },
      ],
      displayLabels: { avgColumn: 'Avg', scaleHigh: 'Strong', scaleLow: 'Weak' },
    } as any,

    // ── 5. Quadrant Map — Comprehension × Conversion Intent ───────────────
    // Spec: "Hero alt. | Quadrant scatter — Comprehension × Conversion-intent"
    // X = Comprehension composite (0-100). Y = Conversion Intent composite (0-100).
    // Quadrant labels guide action: top-right = winner zone.
    // Scales 1-6 LPs: up to 10 scatter points + sidebar items.
    {
      type: 'quadrant-map',
      eyebrow: 'Positioning Map · Comprehension × Conversion Intent',
      actionTitle: 'Redesign B occupies the "Clear & Converts" quadrant alone',
      actionSubtitle: 'Comprehension (X): how well the value prop is understood · Conversion Intent (Y): likelihood to act · n=30',
      xAxisLabel: 'Comprehension',
      yAxisLabel: 'Conversion Intent',
      quadrantLabels: {
        topLeft:    'Converts but confuses',
        topRight:   'Clear & Converts ✓',
        bottomLeft: 'Rebuild',
        bottomRight:'Clear but hesitant',
      },
      midpoints: { x: 60, y: 60 },
      points: [
        { label: 'Redesign B',       x: 72, y: 74, xMetricValue: 72, yMetricValue: 74 },
        { label: 'Current Homepage',  x: 65, y: 61, xMetricValue: 65, yMetricValue: 61 },
        { label: 'Minimal Variant C', x: 54, y: 55, xMetricValue: 54, yMetricValue: 55 },
      ],
      sidebarItems: [
        { label: 'Redesign Concept B',  description: 'Comprehension 72 · Conversion 74. Highest on both dimensions — ready to ship.', badgeLabel: 'B', imageUrl: LP_IMAGE_B },
        { label: 'Current Homepage',    description: 'Comprehension 65 · Conversion 61. Familiar brand signal holds it up but clarity lags.', badgeLabel: 'A', imageUrl: LP_IMAGE_A },
        { label: 'Minimal Variant C',   description: 'Comprehension 54 · Conversion 55. Message not landing — hero needs a rewrite.', badgeLabel: 'C', imageUrl: LP_IMAGE_C },
      ],
    } as any,

    // ── 6. Single Select Bars — 5-second test: % understood per LP ────────
    // Spec: "5-second test | Donut (% understood)"
    // Q1 open-end is classified → % twins who correctly described the offer
    // within 5 seconds. One bar per LP makes the cross-concept comparison
    // instantly legible. (A donut per LP would require multiple chart instances
    // — horizontal bars achieve the same reading in a single view.)
    // Scales 1-6 LPs: one bar per LP, images as visual identifiers.
    {
      type: 'single-select-bars',
      eyebrow: '5-Sec · First Impression',
      actionTitle: '72% of twins grasped Redesign B\'s offer instantly — highest across all variants',
      actionSubtitle: 'Q1 — "In 5 seconds: what is this offering, and who\'s it for?" · classifier: value understood? · n=30',
      plotType: 'single_select_horizontal_bars',
      bars: [
        { id: 'asset_b', label: 'Redesign Concept B — grasped the offer', value: 72, count: 22, isTop: true,  imageUrl: LP_IMAGE_B },
        { id: 'asset_a', label: 'Current Homepage — grasped the offer',   value: 65, count: 20, isTop: false, imageUrl: LP_IMAGE_A },
        { id: 'asset_c', label: 'Minimal Variant C — grasped the offer',  value: 54, count: 16, isTop: false, imageUrl: LP_IMAGE_C },
      ],
      quoteCard: {
        stat: '72%',
        quote: '"Analytics without the setup" — I got it in two seconds. Everything else just confirmed it.',
        attribution: 'Alex Kim, Director of Sales',
      },
    } as any,

    // ── 7. Qual Analysis — Objections (Q7: "what would stop you?") ────────
    // Spec: "Objections | Ranked horizontal bar (themed)"
    // Q7 open-ends are classified into objection themes + severity score.
    // Each themeBlock shows which LP triggered the objection — so the chart
    // is always clear about which concept is being discussed.
    // Scales 1-6 LPs: themes are cross-LP, but every quote names its LP.
    {
      type: 'qual-analysis',
      eyebrow: 'Objections · Q7',
      actionTitle: 'Trust gap and below-fold clutter are the top conversion blockers',
      displayOptions: { layout: 'default', tone: 'negative', themeBlockMode: 'ranked', themeBlockCount: 3 },
      stats: [
        { value: '43%',  label: 'Redesign B — Below-fold overload', count: 43, color: 'accent' },
        { value: '33%',  label: 'Redesign B — Thin social proof',    count: 33, color: 'primary' },
        { value: '27%',  label: 'Minimal C — Message unclear',        count: 27, color: 'muted' },
      ],
      themes: [
        {
          title: 'Below-fold information overload',
          description: 'On Redesign B: the hero is clean but the first scroll reveals too many competing elements — icons, micro-stats, a testimonial block, and a secondary CTA all vie for attention.',
          citationIndices: [0, 1],
          color: 'accent',
        },
        {
          title: 'Thin social proof on Redesign B',
          description: 'Current Homepage carries a logo wall and a 4.8★ chip. Redesign B omits both — several twins said this gap reduced their confidence before they\'d read the CTA.',
          citationIndices: [2, 3],
          color: 'primary',
        },
        {
          title: 'Minimal Variant C — value prop not landing',
          description: 'Without a benefit-led headline or visible social proof, nearly half of Minimal C twins couldn\'t articulate the offer and said they would bounce rather than scroll.',
          citationIndices: [4, 5],
          color: 'accent-alt',
        },
      ],
      themeBlocks: [
        {
          title: 'Below-fold overload · Redesign B',
          reachValue: '43%',
          reachLabel: 'of Redesign B twins flagged it',
          respondentCount: 13,
          tone: 'negative',
          quote: { text: '[Redesign B] The hero is great, but one scroll down and it\'s a wall of stuff. I\'d lose focus. — Marcus Williams, Director of Growth' },
        },
        {
          title: 'Missing trust signals · Redesign B',
          reachValue: '33%',
          reachLabel: 'of Redesign B twins wanted more proof',
          respondentCount: 10,
          tone: 'negative',
          quote: { text: '[Redesign B] Where\'s the logo strip? Without it I don\'t know who uses this. — Emma Thompson, Director of Operations' },
        },
        {
          title: 'Value prop unclear · Minimal Variant C',
          reachValue: '46%',
          reachLabel: 'of Minimal C twins couldn\'t describe the offer',
          respondentCount: 14,
          tone: 'negative',
          quote: { text: '[Minimal C] I wasn\'t sure what they\'re selling — the image is nice but nothing tells me what I get. — Priya Patel, VP of Product' },
        },
      ],
      responses: [
        '[Redesign B] One scroll down and it\'s a wall of stuff.',
        '[Redesign B] The clutter below the fold feels overwhelming.',
        '[Redesign B] No logo strip — I don\'t know who uses this.',
        '[Redesign B] I need to see reviews before I\'d sign up.',
        '[Minimal C] I wasn\'t sure what they\'re selling.',
        '[Minimal C] The hero image is nice but nothing tells me what I get.',
      ],
    } as any,

    // ── 8. Matrix Rating — Full per-metric scorecard ───────────────────────
    // Spec: "Full scorecard | Heatmap table (pages × dimensions)"
    // Rows = LPs (with thumbnail), columns = individual LIFT/MECLABS metrics.
    // LP images in row labels make it always obvious which page is which.
    // Scales 1-6 LPs: rows grow (up to 12 rows before pagination).
    {
      type: 'matrix-rating',
      eyebrow: 'Metric Scorecard',
      actionTitle: 'Redesign B leads on 4 of 5 metrics — only trust favours Current Homepage',
      actionSubtitle: 'Individual metric scores 0-100 · rows = landing pages · n=30',
      plotType: 'matrix_rating_heatmap',
      scale: { min: 0, max: 100, minLabel: 'Weak', maxLabel: 'Strong' },
      measures: [
        { id: 'm_clar',  label: 'Msg. Clarity',      metricId: 'metric_clarity',           unit: 'rating_0_100', average: 70 },
        { id: 'm_rel',   label: 'Relevance',          metricId: 'metric_relevance',         unit: 'rating_0_100', average: 65 },
        { id: 'm_trust', label: 'Trust',              metricId: 'metric_trust',             unit: 'rating_0_100', average: 68 },
        { id: 'm_vishy', label: 'Visual Hierarchy',   metricId: 'metric_visual_hierarchy',  unit: 'rating_0_100', average: 67 },
        { id: 'm_nxst',  label: 'Next-Step Intent',   metricId: 'metric_next_step',         unit: 'rating_0_100', average: 63 },
      ],
      rows: [
        {
          id: 'asset_b', label: 'Redesign Concept B', average: 71.0, imageUrl: LP_IMAGE_B,
          cells: [
            { measureId: 'm_clar',  value: 81, normalizedValue: 0.81, isStrongest: true  },
            { measureId: 'm_rel',   value: 74, normalizedValue: 0.74, isStrongest: true  },
            { measureId: 'm_trust', value: 68, normalizedValue: 0.68, isStrongest: false },
            { measureId: 'm_vishy', value: 72, normalizedValue: 0.72, isStrongest: true  },
            { measureId: 'm_nxst',  value: 74, normalizedValue: 0.74, isStrongest: true  },
          ],
        },
        {
          id: 'asset_a', label: 'Current Homepage', average: 63.0, imageUrl: LP_IMAGE_A,
          cells: [
            { measureId: 'm_clar',  value: 65, normalizedValue: 0.65, isStrongest: false },
            { measureId: 'm_rel',   value: 61, normalizedValue: 0.61, isStrongest: false },
            { measureId: 'm_trust', value: 70, normalizedValue: 0.70, isStrongest: true  },
            { measureId: 'm_vishy', value: 62, normalizedValue: 0.62, isStrongest: false },
            { measureId: 'm_nxst',  value: 61, normalizedValue: 0.61, isStrongest: false },
          ],
        },
        {
          id: 'asset_c', label: 'Minimal Variant C', average: 57.0, imageUrl: LP_IMAGE_C,
          cells: [
            { measureId: 'm_clar',  value: 63, normalizedValue: 0.63, isStrongest: false },
            { measureId: 'm_rel',   value: 59, normalizedValue: 0.59, isStrongest: false },
            { measureId: 'm_trust', value: 66, normalizedValue: 0.66, isStrongest: false },
            { measureId: 'm_vishy', value: 66, normalizedValue: 0.66, isStrongest: false },
            { measureId: 'm_nxst',  value: 55, normalizedValue: 0.55, isStrongest: false },
          ],
        },
      ],
      displayLabels: { avgColumn: 'Avg', scaleHigh: 'Strong', scaleLow: 'Weak' },
    } as any,

    // ── 9. Single Select Bars — Preference tiebreaker (Q16) ──────────────
    // Spec: Q16 preference is an OPTIONAL tiebreaker, asked last.
    // "Podium is derived from scores — preference is only an extra indicator."
    // Labeled clearly: bars show which LP each twin nominated.
    // Scales 1-6 LPs: one bar per LP.
    {
      type: 'single-select-bars',
      eyebrow: 'Preference · Q16',
      actionTitle: '64% of twins nominated Redesign B — consistent with the composite verdict',
      actionSubtitle: '"Which of these pages would most likely get you to take action, and why?" · n=30',
      plotType: 'single_select_horizontal_bars',
      bars: [
        { id: 'asset_b', label: 'Redesign Concept B', value: 64, count: 19, isTop: true,  imageUrl: LP_IMAGE_B },
        { id: 'asset_a', label: 'Current Homepage',   value: 27, count: 8,  isTop: false, imageUrl: LP_IMAGE_A },
        { id: 'asset_c', label: 'Minimal Variant C',  value:  9, count: 3,  isTop: false, imageUrl: LP_IMAGE_C },
      ],
      quoteCard: {
        stat: '64%',
        quote: '[Redesign B] Everything I need is right there without scrolling. The CTA is impossible to miss.',
        attribution: 'Sarah Chen, VP of Marketing',
      },
    } as any,

    // ── 10. Qual Analysis — What resonated (positive themes) ──────────────
    // Spec: "Voice of customer | Quote cards"
    // Themes come from Q9 "what works or doesn't" open-end.
    // Every quote explicitly names the LP the twin was looking at.
    // Twin names (not "Respondent X") from the respondent registry.
    // Scales 1-6 LPs: themes are cross-LP, LP always named in each quote.
    {
      type: 'qual-analysis',
      eyebrow: 'What Resonated',
      actionTitle: 'Clear headline, strong CTA, and visual focus are the top strengths of Redesign B',
      displayOptions: { layout: 'featured-quote-left', tone: 'positive', themeBlockMode: 'ranked', themeBlockCount: 3, showThemeReachAs: 'quote_support' },
      themes: [
        {
          title: 'Clear value proposition — Redesign B',
          description: 'Within 5 seconds, twins exposed to Redesign B could articulate the product\'s core promise unprompted. The hero headline communicates the "who it\'s for" and "what it does" without scrolling.',
          citationIndices: [0, 1, 2],
          color: 'accent',
          keywords: [{ word: 'clear', weight: 1, primary: true }, { word: 'instant', weight: 0.8, primary: false }],
        },
        {
          title: 'Prominent, action-oriented CTA — Redesign B',
          description: 'Button colour, copy ("Start free — no credit card"), and above-fold placement drove the highest CTA recall and click intent scores of any LP in the test.',
          citationIndices: [3, 4],
          color: 'primary',
          keywords: [{ word: 'CTA', weight: 1, primary: true }, { word: 'button', weight: 0.7, primary: false }],
        },
        {
          title: 'Clean visual hierarchy — Redesign B',
          description: 'Minimal nav, single focal image, and white space guided twins naturally toward the CTA. Minimal Variant C shared the white space but lacked a benefit-led headline to anchor the eye.',
          citationIndices: [5, 6],
          color: 'accent-alt',
          keywords: [{ word: 'focused', weight: 1, primary: true }, { word: 'clean', weight: 0.8, primary: false }],
        },
      ],
      themeBlocks: [
        {
          title: 'Clear value prop · Redesign B',
          reachValue: '73%',
          reachLabel: 'of Redesign B twins cited headline clarity',
          respondentCount: 22,
          tone: 'positive',
          quote: { text: '[Redesign B] The headline told me exactly what the product does. I didn\'t have to guess. — James Rodriguez, Engineering Manager' },
        },
        {
          title: 'Strong CTA · Redesign B',
          reachValue: '68%',
          reachLabel: 'would click the Redesign B CTA',
          respondentCount: 20,
          tone: 'positive',
          quote: { text: '[Redesign B] "Start free" — obvious what happens next. I\'d click it without thinking. — Alex Kim, Director of Sales' },
        },
        {
          title: 'Visual focus · Redesign B',
          reachValue: '60%',
          reachLabel: 'praised Redesign B\'s reduced clutter',
          respondentCount: 18,
          tone: 'positive',
          quote: { text: '[Redesign B] Not trying to do too many things at once. Easy to know where to look. — Sarah Chen, VP of Marketing' },
        },
      ],
      featuredQuote: {
        text: '"Analytics without the setup" — I got it in two seconds on Redesign B. Everything else just confirmed it.',
        respondent: { name: 'Alex Kim, Director of Sales' },
      },
      featuredQuoteTone: 'positive',
      responses: [
        '[Redesign B] The headline told me exactly what this does.',
        '[Redesign B] "Analytics without the setup" — got it immediately.',
        '[Redesign B] Start free button is obvious and trustworthy.',
        '[Redesign B] CTA colour made me look at it first.',
        '[Redesign B] Not trying to do too many things at once.',
        '[Minimal C] White space is nice but the headline didn\'t land.',
        '[Current Homepage] Familiar but a bit dated.',
      ],
      sectionLabel: 'Grounded themes and quotes',
    } as any,

    // ── 11. Recommendations ───────────────────────────────────────────────
    {
      type: 'recommendations',
      eyebrow: 'Recommendations',
      actionTitle: 'Ship a stronger page',
      introParagraph: 'Redesign B is the clear winner across Comprehension, Conversion Intent, and Desirability. Ship it with two targeted refinements to close the trust gap and address below-fold overload.',
      introBullets: ['Composite scores are consistent across all seniority segments — there is no risk of a "the winner harms a segment" effect.'],
      actions: [
        {
          stepNumber: 1, icon: '',
          title: 'Ship Redesign B as the primary variant',
          body: 'Replace Current Homepage with Redesign B. The headline, CTA, and visual hierarchy all outperform the current page on Conversion Intent (74 vs 61) and Comprehension (72 vs 65).',
          isHighlight: true,
          sourceIds: ['asset_b'],
        },
        {
          stepNumber: 2, icon: '',
          title: 'Add trust signals from Current Homepage',
          body: 'Add the logo wall and 4.8-star rating chip from Current Homepage into the hero of Redesign B. Trust is the only metric where Current Homepage wins (+2 pts) — grafting it closes that gap.',
          isHighlight: false,
          sourceIds: ['asset_a', 'asset_b'],
        },
        {
          stepNumber: 3, icon: '',
          title: 'Tighten Redesign B below the fold',
          body: '43% of twins flagged information overload in the first scroll section. Consolidate icons + micro-stats into a single "why us" row. Re-test with 15 twins — this should push Conversion Intent above 80.',
          isHighlight: false,
          sourceIds: ['asset_b'],
        },
      ],
    } as any,

    // ── 12. Outro — Methodology & audience ────────────────────────────────
    {
      type: 'outro',
      eyebrow: 'Methodology',
      studyIdentity: {
        title: 'Study overview',
        rows: [
          { label: 'Study type',   value: 'LP Comparison — v1 lean (3 decision scores, segments, open-ends)' },
          { label: 'Variants',     value: '3 — Redesign B (winner) · Current Homepage · Minimal Variant C' },
          { label: 'Completed',    value: new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) },
          { label: 'Scoring',      value: 'Comprehension · Conversion Intent · Desirability (0-100)' },
        ],
      },
      studyInfo: {
        title: 'Fielding',
        rows: [
          { label: 'Simulated twins', value: '30 (persona-grounded digital twins)' },
          { label: 'Exposure',        value: 'Within-respondent — each twin rated all 3 pages' },
          { label: 'Questions',       value: 'Q1–Q16: 5-sec open, 0-100 ratings, objections, next-step intent, preference' },
          { label: 'Open-end classif.', value: 'Q1 → % understood · Q7 → objection severity · Q9, Q15 → themes' },
          { label: 'Engine',          value: 'Fairgen v1' },
        ],
      },
      audienceProfile: {
        title: 'Audience profile',
        rows: [
          { label: 'Target',    value: 'B2B SaaS decision-makers' },
          { label: 'Seniority', value: 'Manager and above (3 tiers)' },
          { label: 'Region',    value: 'US + UK (primary), EU (secondary)' },
          { label: 'Industry',  value: 'Tech, Finance, Professional Services' },
        ],
      },
      distributions: [
        {
          title: 'Seniority split',
          rows: [
            { label: 'C-Suite / VP',  value: 30, count: 9  },
            { label: 'Director',      value: 37, count: 11 },
            { label: 'Manager',       value: 33, count: 10 },
          ],
        },
        {
          title: 'Region split',
          rows: [
            { label: 'United States',  value: 50, count: 15 },
            { label: 'United Kingdom', value: 30, count: 9  },
            { label: 'Europe',         value: 20, count: 6  },
          ],
        },
      ],
    } as any,
  ],
}

// ── Fixture: canonical analysis output ───────────────────────────────────────

const FIXTURE_ANALYSIS: CanonicalAnalysisOutput = {
  schemaVersion: 'canonical-analysis.v1',
  registryVersion: 'questionnaire-registry.v1',
  questionnaireDraftId: 'preview-draft',
  experimentId: 'preview-exp',
  summaryStory: {
    headline: 'Redesign B wins on conversion intent',
    subtitle: 'Clearer headline and stronger CTA drove the gap',
    beats: [],
  } as any,
  recommendationStory: {
    headline: 'Ship Redesign B',
    beats: [],
  } as any,
  metricResults: {
    metric_conversion_intent: {
      metricId: 'metric_conversion_intent',
      questionId: 'q14',
      stableKey: 'conversion_intent',
      baseSize: 30,
      display: { label: 'Conversion Intent', format: 'score' },
      breakdown: { asset_b: 0.74, asset_a: 0.61, asset_c: 0.55 },
    } as any,
    metric_clarity: {
      metricId: 'metric_clarity',
      questionId: 'q2',
      stableKey: 'clarity',
      baseSize: 30,
      display: { label: 'Message Clarity', format: 'score' },
      breakdown: { asset_b: 0.81, asset_a: 0.65, asset_c: 0.63 },
    } as any,
    metric_relevance: {
      metricId: 'metric_relevance',
      questionId: 'q5',
      stableKey: 'relevance',
      baseSize: 30,
      display: { label: 'Relevance', format: 'score' },
      breakdown: { asset_b: 0.74, asset_a: 0.61, asset_c: 0.59 },
    } as any,
    metric_trust: {
      metricId: 'metric_trust',
      questionId: 'q6',
      stableKey: 'trust',
      baseSize: 30,
      display: { label: 'Trust & Credibility', format: 'score' },
      breakdown: { asset_b: 0.68, asset_a: 0.70, asset_c: 0.66 },
    } as any,
    metric_visual_hierarchy: {
      metricId: 'metric_visual_hierarchy',
      questionId: 'q8',
      stableKey: 'visual_hierarchy',
      baseSize: 30,
      display: { label: 'Visual Hierarchy', format: 'score' },
      breakdown: { asset_b: 0.72, asset_a: 0.62, asset_c: 0.66 },
    } as any,
    metric_next_step: {
      metricId: 'metric_next_step',
      questionId: 'q14',
      stableKey: 'next_step',
      baseSize: 30,
      display: { label: 'Next-Step Intent', format: 'score' },
      breakdown: { asset_b: 0.74, asset_a: 0.61, asset_c: 0.55 },
    } as any,
  },
  compositeResults: {
    composite_lp_comprehension: {
      compositeMetricId: 'composite_lp_comprehension',
      winningAssetId: 'asset_b',
      scoresByAssetId: { asset_b: 72, asset_a: 65, asset_c: 54 },
      // componentContributions keyed by assetId; each value is an array of {metricId, weight}
      componentContributions: {
        asset_b: [
          { metricId: 'metric_clarity', weight: 0.4 },
          { metricId: 'metric_visual_hierarchy', weight: 0.3 },
        ],
        asset_a: [
          { metricId: 'metric_clarity', weight: 0.4 },
          { metricId: 'metric_visual_hierarchy', weight: 0.3 },
        ],
        asset_c: [
          { metricId: 'metric_clarity', weight: 0.4 },
          { metricId: 'metric_visual_hierarchy', weight: 0.3 },
        ],
      },
      tieAssetIds: [],
      rankedAssetIds: ['asset_b', 'asset_a', 'asset_c'],
    } as any,
    composite_lp_conversion_intent: {
      compositeMetricId: 'composite_lp_conversion_intent',
      winningAssetId: 'asset_b',
      scoresByAssetId: { asset_b: 74, asset_a: 61, asset_c: 55 },
      // componentContributions keyed by assetId; each value is an array of {metricId, weight}
      componentContributions: {
        asset_b: [
          { metricId: 'metric_relevance', weight: 0.3 },
          { metricId: 'metric_trust', weight: 0.2 },
          { metricId: 'metric_next_step', weight: 0.3 },
        ],
        asset_a: [
          { metricId: 'metric_relevance', weight: 0.3 },
          { metricId: 'metric_trust', weight: 0.2 },
          { metricId: 'metric_next_step', weight: 0.3 },
        ],
        asset_c: [
          { metricId: 'metric_relevance', weight: 0.3 },
          { metricId: 'metric_trust', weight: 0.2 },
          { metricId: 'metric_next_step', weight: 0.3 },
        ],
      },
      tieAssetIds: [],
      rankedAssetIds: ['asset_b', 'asset_a', 'asset_c'],
    } as any,
    composite_lp_desirability: {
      compositeMetricId: 'composite_lp_desirability',
      winningAssetId: 'asset_b',
      scoresByAssetId: { asset_b: 68, asset_a: 64, asset_c: 58 },
      componentContributions: {},
      tieAssetIds: [],
      rankedAssetIds: ['asset_b', 'asset_a', 'asset_c'],
    } as any,
  },
  qualitativeResults: {},
  segmentationResults: {} as any,
  quadrantAxisResults: {
    default: {
      xMetricId: 'composite_lp_comprehension',
      yMetricId: 'composite_lp_conversion_intent',
      xAxisLabel: 'Comprehension',
      yAxisLabel: 'Conversion Intent',
      points: [
        { assetId: 'asset_b', x: 72, y: 74, label: 'Redesign B',       badgeLabel: 'B', imageUrl: LP_IMAGE_B },
        { assetId: 'asset_a', x: 65, y: 61, label: 'Current Homepage',  badgeLabel: 'A', imageUrl: LP_IMAGE_A },
        { assetId: 'asset_c', x: 54, y: 55, label: 'Minimal Variant C', badgeLabel: 'C', imageUrl: LP_IMAGE_C },
      ],
      rationale: 'LP study: used Comprehension and Conversion-Intent composites as axes.',
      excludedMetricReasons: {},
    },
  } as any,
  warnings: [],
  flags: [
    {
      code: 'unstable_winner',
      severity: 'caution',
      scope: 'composite',
      compositeMetricId: 'composite_lp_conversion_intent',
      assetId: 'asset_b',
      title: 'Redesign B leads by 13 pts — edge is real but not emphatic',
      detail:
        'Redesign B Conversion Intent: 74 vs Current Homepage: 61 (Δ=13). The gap is outside the confidence interval for 82% of twins. Directionally clear; expand to 50 twins for a definitive call.',
      data: { margin: 13 },
    },
    {
      code: 'missing_element',
      severity: 'info',
      scope: 'study',
      title: 'Desirability composite is partial — no ad to match',
      detail:
        'Q3 (Message Match) skipped: no originating ad attached to this study. Q4 (Emotional Read) is v2-only. Desirability composite computed from available dimensions only and re-normalised.',
      data: {},
    },
  ],
}

// ── Fixture: executive summary ────────────────────────────────────────────────

const FIXTURE_EXECUTIVE_SUMMARY = {
  headline: 'Redesign B converts better',
  narrative:
    'Across 30 simulated twins, Redesign Concept B outperformed Current Homepage on Conversion Intent (74 vs 61), Comprehension (72 vs 65), and Relevance (74 vs 61). Trust was the only metric where Current Homepage had a marginal edge (+2 pts).',
  metricCards: [
    { title: 'Conversion Intent', type: 'circular' as const, primaryValue: 74, valueType: 'average' as const },
    { title: 'Comprehension',     type: 'circular' as const, primaryValue: 72, valueType: 'average' as const },
    { title: 'Message Clarity',      type: 'circular' as const, primaryValue: 81, valueType: 'average' as const },
    { title: 'Trust & Credibility',  type: 'circular' as const, primaryValue: 68, valueType: 'average' as const },
    { title: 'Next-Step Intent',     type: 'circular' as const, primaryValue: 74, valueType: 'average' as const },
  ],
  featuredQuotes: [
    {
      text: '[Redesign B] The headline told me what the product does. I didn\'t have to guess.',
      sentiment: 'positive' as const,
      attribution: 'James Rodriguez, Engineering Manager',
    },
    {
      text: '[Redesign B] "Start free" — obvious what happens next. I\'d click it without thinking.',
      sentiment: 'positive' as const,
      attribution: 'Alex Kim, Director of Sales',
    },
    {
      text: '[Redesign B] The hero is great, but one scroll down and it\'s a wall of stuff. I\'d lose focus.',
      sentiment: 'negative' as const,
      attribution: 'Marcus Williams, Director of Growth',
    },
  ],
}

// ── Page ─────────────────────────────────────────────────────────────────────

// Placeholder image simulating an uploaded landing page screenshot.
// Replace with a real URL to preview with an actual cover image.
const FIXTURE_COVER_IMAGE_URL = 'https://placehold.co/800x500/e8ecf0/94a3b8?text=Cover+Image'

// ── Fixture: tested assets block ─────────────────────────────────────────────
//
// One landing-page asset so the hero's asset stack and preview dialog render
// in this preview too, not just the deck slides above.
const FIXTURE_SURVEY = {
  testedAssets: {
    mode: 'all_assets',
    required: true,
    assetTypes: ['landing_page'],
    assets: [
      {
        id: 'lp-fixture-1',
        type: 'landing_page',
        stableKey: 'lp-fixture-1',
        name: 'Fixture landing page',
        description: 'Hero and pricing above the fold.',
        media: [],
        url: 'https://example.com',
        pageUrl: 'https://example.com',
        headline: 'Ship faster',
        primaryCallToAction: 'Start free',
        metadata: {},
        createdAt: '2026-01-01T00:00:00.000Z',
        updatedAt: '2026-01-01T00:00:00.000Z',
      },
    ],
    validation: { status: 'not_validated', issues: [] },
  },
}

export default function LPStudyPreviewPage() {
  return (
    <ReportLandingPage
      studyId="preview-lp-study"
      studyName="Homepage Redesign A/B Test"
      createdAt={new Date()}
      survey={FIXTURE_SURVEY}
      responses={[]}
      goalId="lp_conversion"
      executiveSummary={FIXTURE_EXECUTIVE_SUMMARY}
      canonicalAnalysis={FIXTURE_ANALYSIS}
      deck={FIXTURE_DECK}
      reportStatus="ready"
      reportHeadline="Redesign B converts better"
      isDeckLoading={false}
      coverImageUrl={FIXTURE_COVER_IMAGE_URL}
    />
  )
}
